/**
 * Home page.
 * Renders a tabbed interface whose content is lazy-loaded from the
 * home content registry (data-file pattern).
 *
 * @module pages/home
 */

import React, { Suspense, lazy, useMemo } from 'react';
import { useSearchParams }                from 'react-router-dom';
import Layout                             from '../components/Layout';
import { useI18n }                        from '../dataProviders/I18nProvider';
import homeContentRegistry                from './home/homeContentRegistry';

/** Tab identifiers (derived from the registry). */
const TABS = homeContentRegistry.map((entry) => entry.tab);

/**
 * Renders the Home page with lazy-loaded tab content.
 *
 * @returns {React.ReactElement}
 */
export default function Home() {
  const { t, locale }                    = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam  = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'overview';

  const TabContent = useMemo(() => {
    const entry  = homeContentRegistry.find((e) => e.tab === activeTab);
    if (!entry) return null;
    const loader = entry.load[locale] || entry.load['en'];
    if (!loader) return null;
    return lazy(loader);
  }, [activeTab, locale]);

  function handleTabChange(tab) {
    setSearchParams({ tab }, { replace: true });
  }

  return (
    <Layout title="">
      {/* Tab bar */}
      <div className="home-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`home-tab${activeTab === tab ? ' home-tab--active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            {t(`home.tab.${tab}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {TabContent && (
        <Suspense fallback={<div className="home-placeholder">{t('home.loading')}</div>}>
          <TabContent />
        </Suspense>
      )}
    </Layout>
  );
}
